import {
  computeDueDateFromPaymentCondition,
  parsePaymentConditionDays,
} from "./payment-due-date";
import {
  formatFinancialStatusValue,
  normalizeStoredFinancialStatusValue,
} from "./freight-v2-table-utils";

const DEFAULT_FINANCIAL_STATUS = "pendente";

function roundCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount * 100) / 100;
}

export function normalizeBoletoAttachment(attachment) {
  if (attachment == null || attachment === "") return null;
  if (typeof attachment === "string") return attachment;

  if (typeof attachment === "object") {
    return {
      ...attachment,
      url: attachment.url || attachment.downloadUrl || null,
      name:
        attachment.name ||
        attachment.originalName ||
        attachment.fileName ||
        null,
    };
  }

  return null;
}

export function resolveInstallmentBoletoAttachment({
  financialInstallments,
  installmentNumber,
  rootBoletoAttachment,
}) {
  const normalizedNumber = Number(installmentNumber) || 1;
  const installments = Array.isArray(financialInstallments)
    ? financialInstallments
    : [];
  const installment = installments.find(
    (item) => Number(item?.installmentNumber) === normalizedNumber,
  );
  const fromInstallment = normalizeBoletoAttachment(
    installment?.boletoAttachment,
  );
  if (fromInstallment) return fromInstallment;

  if (normalizedNumber === 1) {
    return normalizeBoletoAttachment(rootBoletoAttachment);
  }

  return null;
}

export function formatInstallmentLabel(installmentNumber, installmentTotal) {
  const current = Number(installmentNumber) || 1;
  const total = Number(installmentTotal) || 1;
  return `${current}/${total}`;
}

export function resolveInstallmentRowId(freightId, installmentNumber) {
  return `${freightId}__parcela__${installmentNumber}`;
}

export function getPaymentConditionInstallmentCount(paymentCondition) {
  if (!paymentCondition) return 1;

  const fromArray = Array.isArray(paymentCondition.paymentInstallments)
    ? paymentCondition.paymentInstallments.length
    : 0;
  if (fromArray > 0) return fromArray;

  const fromField = Number(paymentCondition.installments);
  if (Number.isFinite(fromField) && fromField > 0) return Math.floor(fromField);

  const days = parsePaymentConditionDays(paymentCondition.description);
  return days.length > 0 ? days.length : 1;
}

function getInstallmentPercentages(paymentCondition, totalInstallments) {
  const saved = Array.isArray(paymentCondition?.paymentInstallments)
    ? paymentCondition.paymentInstallments
    : [];

  if (saved.length > 0) {
    return saved.map((item) => Number(item?.percentage) || 0);
  }

  if (totalInstallments <= 1) return [100];

  const equal = roundCurrency(100 / totalInstallments);
  const percentages = Array.from({ length: totalInstallments }, () => equal);
  const diff = roundCurrency(100 - percentages.reduce((sum, item) => sum + item, 0));
  if (diff !== 0) {
    percentages[percentages.length - 1] = roundCurrency(
      percentages[percentages.length - 1] + diff,
    );
  }
  return percentages;
}

function normalizeSavedInstallment(item, index, total) {
  const installmentNumber = Number(item?.installmentNumber) || index + 1;
  const installmentTotal = Number(item?.installmentTotal) || total;
  const statusFinancial =
    normalizeStoredFinancialStatusValue(
      item?.statusFinancial ?? item?.financialStatus,
      DEFAULT_FINANCIAL_STATUS,
    ) || DEFAULT_FINANCIAL_STATUS;

  return {
    installmentNumber,
    installmentTotal,
    installmentLabel: formatInstallmentLabel(
      installmentNumber,
      installmentTotal,
    ),
    dueDate: item?.dueDate ?? null,
    value: roundCurrency(item?.value ?? 0),
    percentage: Number(item?.percentage) || null,
    statusFinancial,
    financialStatus:
      formatFinancialStatusValue(item?.financialStatus ?? statusFinancial) ||
      formatFinancialStatusValue(statusFinancial),
    settlementDate: item?.settlementDate ?? item?.dataBaixa ?? null,
    dataBaixa: item?.dataBaixa ?? item?.settlementDate ?? null,
    boletoAttachment: item?.boletoAttachment ?? null,
  };
}

export function buildFinancialInstallmentsForFreight(
  freight,
  paymentCondition = null,
) {
  const freightValue = Number(
    freight?.freight?.valueFreightage ?? freight?.valueFreightage ?? 0,
  );
  const dueDateInstallments = Array.isArray(freight?.dueDateInstallments)
    ? freight.dueDateInstallments.filter(Boolean)
    : [];
  const fallbackDue = computeDueDateFromPaymentCondition(
    freight?.paymentConditionLabel,
    freight?.firstDelivery?.dateColeta,
  );
  const dueDates =
    dueDateInstallments.length > 0
      ? dueDateInstallments
      : fallbackDue?.dueDateInstallments ?? [];

  const totalInstallments = Math.max(
    dueDates.length,
    getPaymentConditionInstallmentCount(paymentCondition),
    1,
  );
  const percentages = getInstallmentPercentages(
    paymentCondition,
    totalInstallments,
  );
  const savedInstallments = Array.isArray(freight?.financialInstallments)
    ? freight.financialInstallments
    : [];
  const savedByNumber = new Map(
    savedInstallments.map((item, index) => [
      Number(item?.installmentNumber) || index + 1,
      item,
    ]),
  );

  const defaultStatus = DEFAULT_FINANCIAL_STATUS;
  const inheritedStatus =
    normalizeStoredFinancialStatusValue(
      freight?.statusFinancial ?? freight?.financialStatus,
      defaultStatus,
    ) || defaultStatus;

  return Array.from({ length: totalInstallments }, (_, index) => {
    const installmentNumber = index + 1;
    const saved = savedByNumber.get(installmentNumber);
    const percentage = percentages[index] ?? percentages[percentages.length - 1] ?? 100;
    const proportionalValue = roundCurrency((freightValue * percentage) / 100);

    const base = {
      installmentNumber,
      installmentTotal: totalInstallments,
      installmentLabel: formatInstallmentLabel(
        installmentNumber,
        totalInstallments,
      ),
      dueDate: dueDates[index] ?? dueDates[dueDates.length - 1] ?? freight?.dueDate ?? null,
      value: proportionalValue,
      percentage,
      statusFinancial: inheritedStatus,
      financialStatus: formatFinancialStatusValue(inheritedStatus),
      settlementDate: null,
      dataBaixa: null,
      boletoAttachment: null,
    };

    if (!saved) return base;

    return normalizeSavedInstallment(
      {
        ...base,
        ...saved,
        installmentNumber,
        installmentTotal: totalInstallments,
        installmentLabel: formatInstallmentLabel(
          installmentNumber,
          totalInstallments,
        ),
        percentage: saved?.percentage ?? percentage,
        value:
          saved?.value != null && saved?.value !== ""
            ? saved.value
            : proportionalValue,
      },
      index,
      totalInstallments,
    );
  });
}

export function expandFreightToInstallmentRows(freight, paymentCondition = null) {
  const installments = buildFinancialInstallmentsForFreight(
    freight,
    paymentCondition,
  );

  return installments.map((installment) => ({
    ...freight,
    id: resolveInstallmentRowId(freight.id, installment.installmentNumber),
    freightId: freight.id,
    installmentNumber: installment.installmentNumber,
    installmentTotal: installment.installmentTotal,
    installmentLabel: installment.installmentLabel,
    isInstallmentRow: installments.length > 1,
    dueDate: installment.dueDate,
    statusFinancial: installment.statusFinancial,
    financialStatus: installment.financialStatus,
    settlementDate: installment.settlementDate,
    dataBaixa: installment.dataBaixa,
    boletoAttachment: installment.boletoAttachment,
    installmentValue: installment.value,
    installmentPercentage: installment.percentage,
    financialInstallments: installments,
    freight: {
      ...freight.freight,
      valueFreightage: installment.value,
    },
  }));
}

export function expandFreightsToInstallmentRows(freights) {
  return (freights || []).flatMap((freight) =>
    expandFreightToInstallmentRows(freight),
  );
}

export function mergeInstallmentUpdate(
  existingInstallments = [],
  installmentNumber,
  patch = {},
) {
  const normalizedNumber = Number(installmentNumber);
  if (!Number.isFinite(normalizedNumber) || normalizedNumber < 1) {
    return existingInstallments;
  }

  return existingInstallments.map((item) => {
    if (Number(item.installmentNumber) !== normalizedNumber) return item;

    const statusFinancial =
      normalizeStoredFinancialStatusValue(
        patch.statusFinancial ?? patch.financialStatus ?? item.statusFinancial,
        DEFAULT_FINANCIAL_STATUS,
      ) || DEFAULT_FINANCIAL_STATUS;

    return {
      ...item,
      dueDate: patch.dueDate ?? item.dueDate,
      value: patch.value != null ? roundCurrency(Number(patch.value)) : item.value,
      statusFinancial,
      financialStatus:
        formatFinancialStatusValue(patch.financialStatus) ||
        formatFinancialStatusValue(statusFinancial),
      settlementDate:
        statusFinancial === "pago"
          ? patch.settlementDate ?? patch.dataBaixa ?? item.settlementDate
          : null,
      dataBaixa:
        statusFinancial === "pago"
          ? patch.dataBaixa ?? patch.settlementDate ?? item.dataBaixa
          : null,
      boletoAttachment: patch.boletoAttachment ?? item.boletoAttachment ?? null,
    };
  });
}

export function getPaymentConditionInstallmentSummary(paymentCondition) {
  const count = getPaymentConditionInstallmentCount(paymentCondition);
  const installments = Array.isArray(paymentCondition?.paymentInstallments)
    ? paymentCondition.paymentInstallments
    : [];

  return {
    count,
    installments,
  };
}
